import React, { useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtDateTime, fmtDate } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Smartphone, CalendarClock, History, Loader2, AlertTriangle } from "lucide-react";

function ControlledDialog({ title, description, icon: Icon, testId, label, inputType, current, submit, children }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState(null);
  const reset = (o) => { setOpen(o); setValue(""); setReason(""); setWarn(null); };
  const go = async (confirm) => {
    setBusy(true);
    try { await submit(value, reason, confirm); toast.success(`${title} saved`); reset(false); }
    catch (ex) {
      const d = ex.response?.data?.detail;
      if (ex.response?.status === 409 && d?.matches) setWarn(d.matches);
      else toast.error(apiError(d));
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="icon" variant="ghost" className="h-8 w-8" title={title} onClick={() => reset(true)} data-testid={`${testId}-btn`}><Icon className="h-4 w-4" /></Button>
      <Dialog open={open} onOpenChange={reset}>
        <DialogContent data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{description}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">Current: <span className="font-mono font-semibold" data-testid={`${testId}-current`}>{current || "—"}</span></div>
            <div><Label className="text-xs">{label} *</Label><Input type={inputType} className="mt-1" value={value} onChange={(e) => { setValue(e.target.value); setWarn(null); }} data-testid={`${testId}-input`} /></div>
            <div><Label className="text-xs">Reason for change *</Label><Textarea className="mt-1" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testId}-reason`} /></div>
            {children}
            {warn && (
              <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 p-3 text-sm" data-testid={`${testId}-duplicate-warning`}>
                <div className="flex items-center gap-1.5 font-semibold mb-1"><AlertTriangle className="h-4 w-4" />This number is already on other records</div>
                <ul className="list-disc pl-5">{warn.map((m) => <li key={m.id}>{m.type}: {m.name} ({m.ref})</li>)}</ul>
                <div className="mt-1 text-xs">Records will NOT be merged. Save anyway?</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button disabled={busy || !value || reason.trim().length < 5} onClick={() => go(!!warn)} data-testid={`${testId}-submit`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : warn ? "Save anyway" : "Confirm change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ChangeMobileDialog({ lead, leadType, onDone }) {
  return (
    <ControlledDialog title="Change Mobile" icon={Smartphone} testId="change-mobile" label="New 10-digit mobile" inputType="tel" current={lead.mobile}
      description="Admin/manager only. The old and new number and your reason are saved in history."
      submit={async (v, reason, confirm) => {
        await api.post("/controlled/lead-mobile", { lead_type: leadType, lead_id: lead.id, new_mobile: v, reason, confirm_duplicate: confirm });
        onDone();
      }} />
  );
}

export function ChangeExpiryDialog({ policy, onDone }) {
  return (
    <ControlledDialog title="Change Expiry" icon={CalendarClock} testId="change-expiry" label="New expiry date" inputType="date" current={fmtDate(policy.expiry_date)}
      description="Admin/manager only. This changes the CRM record and renewal schedule only. It does not change the insurer's official policy."
      submit={async (v, reason) => { await api.post("/controlled/policy-expiry", { policy_id: policy.id, new_expiry_date: v, reason }); onDone(); }} />
  );
}

export function ChangeHistoryDialog({ entityId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const show = async () => {
    setOpen(true); setRows(null);
    try { const { data } = await api.get("/controlled/history", { params: { entity_id: entityId } }); setRows(data); }
    catch (ex) { toast.error(apiError(ex.response?.data?.detail)); setRows([]); }
  };
  return (
    <>
      <Button size="icon" variant="ghost" className="h-8 w-8" title="Change history" onClick={show} data-testid="change-history-btn"><History className="h-4 w-4" /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="change-history-dialog">
          <DialogHeader><DialogTitle>Change History</DialogTitle></DialogHeader>
          {!rows ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : rows.length === 0 ? <p className="text-sm text-muted-foreground">No controlled changes yet.</p> : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {rows.map((r) => (
                <div key={r.id} className="rounded-lg border border-border p-3 text-sm" data-testid="change-history-row">
                  <div className="font-semibold">{r.field === "mobile" ? "Mobile" : "Expiry date"}: <span className="font-mono">{r.old_value || "—"}</span> → <span className="font-mono">{r.new_value}</span></div>
                  <div className="text-muted-foreground">Reason: {r.reason}</div>
                  <div className="text-xs text-muted-foreground">{r.changed_by_name} · {fmtDateTime(r.changed_at)}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
